<script setup lang="ts">
import { computed, nextTick, onMounted, onUnmounted, ref, watch } from "vue";
import { useRoute, useRouter } from "vue-router";
import { AlertTriangle, BarChart3, Bell, Building2, Check, CheckCircle2, ChevronDown, ChevronRight, Info, LogOut, Plus, Settings2, ShieldCheck, UsersRound } from "lucide-vue-next";
import { signOut } from "firebase/auth";
import { useWorkspaceStore } from "@/stores/workspace";
import { firebaseAuth, firebaseConfigured } from "@/services/firebase";
import VoltButton from "@/volt/Button.vue";
import LiveIndicator from "@/components/status/LiveIndicator.vue";
import LanguageSelect from "@/components/layout/LanguageSelect.vue";
import { siteConnectionState, siteConnectionTone } from "@/domain/site-connection";
import { t } from "@/i18n";

const route = useRoute();
const router = useRouter();
const store = useWorkspaceStore();
const activeMenu = ref<"site" | "notifications" | "user" | null>(null);
const headerElement = ref<HTMLElement>();
const popoverElement = ref<HTMLElement>();
const siteMenuButton = ref<HTMLElement>();
const notificationsMenuButton = ref<HTMLElement>();
const userMenuButton = ref<HTMLElement>();
const popoverStyle = ref<Record<string, string>>({});
const activeSection = computed(() => String(route.name ?? ""));
const systemStatus = computed(() => {
  if (!firebaseConfigured) return { label: t("system.demo"), tone: "neutral" as const };
  if (store.workspaceLoading) return { label: t("system.loading"), tone: "warning" as const };
  if (store.workspaceError || store.liveSyncStatus === "error") return { label: t("system.check"), tone: "danger" as const };
  if (store.connectorHealth.state === "missing_key") return { label: t("system.accountInactive"), tone: "danger" as const };
  if (store.connectorHealth.state === "missing_graph") return { label: t("system.graphMissing"), tone: "danger" as const };
  if (store.connectorHealth.state === "stale_connector") return { label: t("system.connectorStale"), tone: "warning" as const };
  if (store.connectorHealth.state === "no_recent_traffic") return { label: t("system.noTraffic"), tone: "warning" as const };
  if (store.connectorHealth.state === "limited_resolution") return { label: t("system.limited"), tone: "warning" as const };
  if (store.liveSyncStatus === "connecting") return { label: t("system.connecting"), tone: "warning" as const };
  if (store.liveSyncStatus === "live") return { label: t("system.live"), tone: "live" as const };
  return { label: t("system.current"), tone: "neutral" as const };
});
// Customers see what they can act on; platform/operations notices need quality rights.
const notifications = computed(() => {
  const health = store.connectorHealth;
  const operator = store.canViewQuality;
  return [
    ...(store.workspaceError ? [{ tone: "danger", title: "Daten gerade nicht erreichbar", detail: store.workspaceError }] : []),
    ...(store.liveSyncError ? [{ tone: "warning", title: "Live-Aktualisierung unterbrochen", detail: "Neue Besuche erscheinen nach dem nächsten Neuladen." }] : []),
    ...(operator && store.traceError ? [{ tone: "warning", title: "Diagnosedaten nicht lesbar", detail: store.traceError }] : []),
    ...(health.state === "missing_key" ? [{ tone: "danger", title: "Website noch nicht verbunden", detail: "Bitte die Einbindung unter Einstellungen → Websites abschließen." }] : []),
    ...(health.state === "no_recent_traffic" ? [{ tone: "warning", title: "Seit 24 Stunden keine Besuche", detail: "Prüfe, ob das Moonshot-Tag noch auf der Website eingebunden ist." }] : []),
    ...(operator && health.state === "missing_graph" ? [{ tone: "danger", title: "Firmenerkennung eingeschränkt", detail: "Kein aktiver, signierter Erkennungsgraph für diese Website." }] : []),
    ...(operator && health.state === "stale_connector" ? [{
      tone: "warning",
      title: "Tracker-Version veraltet",
      detail: health.connectorOutdated
        ? `Zuletzt ${health.lastConnectorVersion ?? "unbekannt"}, erforderlich ${health.requiredConnectorVersion}.`
        : `Verarbeitet noch ${health.lastGraphReleaseId ?? "einen alten Graphen"} statt ${health.graphReleaseId ?? "des aktiven Graphen"}.`
    }] : []),
    ...(operator && health.state === "limited_resolution" ? [{ tone: "warning", title: "Erkennung eingeschränkt", detail: `Connector ${health.requiredConnectorVersion} erforderlich.` }] : []),
    ...(operator && health.graphExpiresSoon ? [{ tone: "warning", title: "Erkennungsgraph läuft bald ab", detail: `Noch rund ${health.graphRemainingHours ?? "wenige"} Stunden gültig.` }] : [])
  ];
});
const userInitials = computed(() => {
  const email = firebaseAuth?.currentUser?.email;
  if (!email) return firebaseConfigured ? "U" : "D";
  return email.split("@")[0].split(/[._-]/).filter(Boolean).slice(0, 2).map((part) => part[0]?.toUpperCase()).join("") || "U";
});
function siteStatus(site: typeof store.availableSites[number]) {
  const state = siteConnectionState(site);
  const labels = {
    degraded: t("header.siteDegraded"),
    connected: t("header.siteConnected"),
    waiting: t("header.siteWaiting"),
    stale: t("header.siteStale"),
    paused: t("header.sitePaused"),
    error: t("header.siteError")
  };
  return { label: labels[state], tone: siteConnectionTone(state) };
}
function updatePopoverPosition() {
  const trigger = activeMenu.value === "site" ? siteMenuButton.value
    : activeMenu.value === "notifications" ? notificationsMenuButton.value
      : activeMenu.value === "user" ? userMenuButton.value : undefined;
  const rect = trigger?.getBoundingClientRect();
  if (!rect) return;
  const width = Math.min(activeMenu.value === "site" ? 340 : activeMenu.value === "user" ? 300 : 340, window.innerWidth - 24);
  // Menus open under their trigger, right edges aligned, so they never cover the page's left side.
  const left = Math.min(Math.max(12, rect.right - width), window.innerWidth - width - 12);
  popoverStyle.value = { top: `${rect.bottom + 8}px`, left: `${left}px`, width: `${width}px` };
}
/** Only one header menu at a time; the language menu is a separate component. */
const MENU_EVENT = "moonshot:header-menu";
function announceMenu(name: string) { window.dispatchEvent(new CustomEvent(MENU_EVENT, { detail: name })); }
function closeForOtherMenu(event: Event) { if ((event as CustomEvent).detail !== "header") activeMenu.value = null; }
function siteInitial(name: string) { return name.trim().charAt(0).toUpperCase() || "W"; }
async function toggleMenu(menu: NonNullable<typeof activeMenu.value>) {
  activeMenu.value = activeMenu.value === menu ? null : menu;
  if (activeMenu.value) announceMenu("header");
  await nextTick();
  updatePopoverPosition();
}
async function navigate(path: string) { activeMenu.value = null; await router.push(path); }
async function logout() { if (firebaseAuth) await signOut(firebaseAuth); activeMenu.value = null; }
async function switchSite(siteId: string) { await store.switchSite(siteId); activeMenu.value = null; }
async function switchOrganization(tenantId: string) {
  try { await store.switchOrganization(tenantId); activeMenu.value = null; }
  catch (error) { store.workspaceError = error instanceof Error ? error.message : "Organisationswechsel fehlgeschlagen."; }
}
function closeOnOutside(event: PointerEvent) {
  if (activeMenu.value && event.target instanceof Node && !headerElement.value?.contains(event.target) && !popoverElement.value?.contains(event.target)) activeMenu.value = null;
}
function closeOnEscape(event: KeyboardEvent) {
  if (event.key !== 'Escape' || !activeMenu.value) return;
  const trigger = activeMenu.value === 'site' ? siteMenuButton.value : activeMenu.value === 'user' ? userMenuButton.value : notificationsMenuButton.value?.querySelector('button');
  activeMenu.value = null; trigger?.focus();
}
watch(() => route.fullPath, () => { activeMenu.value = null; });
onMounted(async () => {
  window.addEventListener("pointerdown", closeOnOutside);
  window.addEventListener("keydown", closeOnEscape);
  window.addEventListener("resize", updatePopoverPosition);
  window.addEventListener("scroll", updatePopoverPosition, true);
  window.addEventListener(MENU_EVENT, closeForOtherMenu);
  await store.refreshOrganizations().catch(() => undefined);
  await store.refreshWorkspace();
  await store.startLiveUpdates();
});
onUnmounted(() => {
  store.stopLiveUpdates();
  window.removeEventListener("pointerdown", closeOnOutside);
  window.removeEventListener("keydown", closeOnEscape);
  window.removeEventListener("resize", updatePopoverPosition);
  window.removeEventListener("scroll", updatePopoverPosition, true);
  window.removeEventListener(MENU_EVENT, closeForOtherMenu);
});
</script>

<template>
  <div class="app-shell">
    <header ref="headerElement" class="app-header">
      <RouterLink to="/visitors" class="brand" aria-label="Moonshot Startseite">
        <span class="brand__mark"><i /><i /><i /></span>
      </RouterLink>
      <nav class="main-nav" :aria-label="t('nav.main')">
        <RouterLink to="/visitors" :class="{ 'is-active': activeSection === 'visitors' }" :aria-current="activeSection === 'visitors' ? 'page' : undefined"><UsersRound /><span>{{ t('nav.visitors') }}</span></RouterLink>
        <RouterLink to="/statistics" :class="{ 'is-active': activeSection === 'statistics' }" :aria-current="activeSection === 'statistics' ? 'page' : undefined"><BarChart3 /><span>{{ t('nav.statistics') }}</span></RouterLink>
        <RouterLink v-if="store.canViewQuality" to="/quality" :class="{ 'is-active': activeSection === 'quality' }" :aria-current="activeSection === 'quality' ? 'page' : undefined"><ShieldCheck /><span>{{ t('nav.quality') }}</span></RouterLink>
        <RouterLink to="/settings" :class="{ 'is-active': activeSection === 'settings' }" :aria-current="activeSection === 'settings' ? 'page' : undefined"><Settings2 /><span>{{ t('nav.settings') }}</span></RouterLink>
      </nav>
      <div class="header-utilities">
        <div class="header-context">
          <button ref="siteMenuButton" class="site-switcher" :aria-label="t('header.switchSite')" :title="store.siteName" :aria-expanded="activeMenu === 'site'" @click="toggleMenu('site')"><span class="site-dot">M</span><strong>{{ store.siteName }}</strong><ChevronDown :size="14" /></button>
        </div>
        <div class="header-actions">
          <LanguageSelect />
          <span class="header-health" :aria-label="systemStatus.label"><LiveIndicator :label="systemStatus.label" :tone="systemStatus.tone" compact icon-only /></span>
          <span ref="notificationsMenuButton"><VoltButton icon-only variant="ghost" :aria-label="t('header.notifications')" :aria-expanded="activeMenu === 'notifications'" @click="toggleMenu('notifications')"><Bell :size="17" /></VoltButton></span>
          <button ref="userMenuButton" class="user-avatar" :aria-label="t('header.userMenu')" :aria-expanded="activeMenu === 'user'" @click="toggleMenu('user')">{{ userInitials }}</button>
        </div>
      </div>
      <Teleport to="body"><Transition name="hp-pop"><aside v-if="activeMenu" ref="popoverElement" class="header-popover hp" :class="`header-popover--${activeMenu}`" :style="popoverStyle" role="dialog" :aria-label="`${activeMenu} Menü`">
        <template v-if="activeMenu === 'site'">
          <header class="hp-header"><strong>{{ t('header.siteOverview') }}</strong><small>{{ t('header.siteOverviewHelp') }}</small></header>
          <div class="hp-list header-site-list">
            <button v-for="site in store.availableSites" :key="site.id" type="button" class="hp-row header-site-option" :class="{ 'is-current': site.id === store.siteId }" :aria-current="site.id === store.siteId ? 'true' : undefined" @click="switchSite(site.id)">
              <span class="hp-mark">{{ siteInitial(site.name) }}</span>
              <span class="hp-text"><strong>{{ site.name }}</strong><small><span class="hp-domain">{{ site.primaryDomain || site.id }}</span><span :class="['hp-status', `status-${siteStatus(site).tone}`]"><i aria-hidden="true" />{{ siteStatus(site).label }}</span></small></span>
              <Check v-if="site.id === store.siteId" class="hp-check" :size="15" aria-hidden="true" />
            </button>
            <p v-if="!store.availableSites.length" class="hp-empty-line">{{ t('header.noSites') }}</p>
          </div>
          <footer class="hp-footer is-quiet">
            <button type="button" class="hp-link" @click="navigate('/settings?section=sites&wizard=new')"><Plus :size="14" aria-hidden="true" /> {{ t('header.addSite') }}</button>
            <button type="button" class="hp-link is-muted" @click="navigate('/settings?section=sites')"><Settings2 :size="14" aria-hidden="true" /> {{ t('header.manageSites') }}</button>
          </footer>
        </template>
        <template v-else-if="activeMenu === 'notifications'">
          <header class="hp-header"><strong>{{ t('header.notifications') }}</strong><small v-if="notifications.length">{{ notifications.length }} {{ notifications.length === 1 ? 'Hinweis' : 'Hinweise' }}</small></header>
          <ul v-if="notifications.length" class="hp-notices">
            <li v-for="notification in notifications" :key="notification.title" :class="['hp-notice', `is-${notification.tone}`]">
              <AlertTriangle v-if="notification.tone !== 'info'" :size="16" aria-hidden="true" /><Info v-else :size="16" aria-hidden="true" />
              <span><strong>{{ notification.title }}</strong><p>{{ notification.detail }}</p></span>
            </li>
          </ul>
          <div v-else class="hp-empty"><CheckCircle2 :size="22" aria-hidden="true" /><strong>{{ t('header.noCritical') }}</strong><p>{{ firebaseConfigured ? t('header.workspaceReachable') : t('header.demo') }}</p></div>
          <footer v-if="store.canViewQuality" class="hp-footer is-link"><button type="button" class="hp-link" @click="navigate('/quality')">{{ t('nav.quality') }} <ChevronRight :size="14" aria-hidden="true" /></button></footer>
        </template>
        <template v-else>
          <header class="hp-user">
            <span class="hp-avatar">{{ userInitials }}</span>
            <span class="hp-text"><strong>{{ firebaseConfigured ? firebaseAuth?.currentUser?.email : t('header.demo') }}</strong><small>{{ firebaseConfigured ? t('header.rolesActive') : 'Ohne Anmeldung' }}</small></span>
          </header>
          <p v-if="store.organizations.length === 1" class="hp-org">
            <Building2 :size="14" aria-hidden="true" /><span>{{ store.organizations[0].name }}</span><small>{{ store.organizations[0].roles.includes('read_only') ? t('header.readOnly') : t('header.admin') }}</small>
          </p>
          <section v-else-if="store.organizations.length" class="hp-section">
            <span class="hp-section__label">{{ t('header.organization') }}</span>
            <div class="hp-list header-organization-list">
              <button v-for="organization in store.organizations" :key="organization.id" type="button" class="hp-row" :class="{ 'is-current': organization.active }" :aria-current="organization.active ? 'true' : undefined" @click="switchOrganization(organization.id)">
                <span class="hp-mark is-icon"><Building2 :size="15" aria-hidden="true" /></span>
                <span class="hp-text"><strong>{{ organization.name }}</strong><small>{{ organization.roles.includes('read_only') ? t('header.readOnly') : t('header.admin') }}</small></span>
                <Check v-if="organization.active" class="hp-check" :size="15" aria-hidden="true" />
              </button>
            </div>
          </section>
          <footer class="hp-footer is-stacked">
            <button type="button" class="hp-menu-item" @click="navigate('/settings')"><Settings2 :size="15" aria-hidden="true" /> {{ t('nav.settings') }}</button>
            <button v-if="firebaseConfigured" type="button" class="hp-menu-item is-danger header-logout-action" @click="logout"><LogOut :size="15" aria-hidden="true" /> {{ t('header.logout') }}</button>
            <button v-else type="button" class="hp-menu-item" @click="navigate('/settings')"><ShieldCheck :size="15" aria-hidden="true" /> {{ t('header.connectFirebase') }}</button>
          </footer>
        </template>
      </aside></Transition></Teleport>
    </header>
    <div v-if="store.workspaceError" class="workspace-error-banner" role="alert"><strong>{{ t('workspace.unavailable') }}</strong> {{ store.workspaceError }} <button @click="store.refreshWorkspace()">{{ t('common.retry') }}</button></div>
    <main class="app-main"><section v-if="activeSection === 'quality' && !store.canViewQuality" class="access-restricted" role="status"><ShieldCheck :size="28" /><h1>Qualitätsbereich</h1><p>Dieser Bereich ist nur mit administrativen oder ausdrücklich vergebenen Qualitätsrechten zugänglich.</p><RouterLink class="v-button" to="/visitors">Zu den Aktivitäten</RouterLink></section><RouterView v-else /></main>
  </div>
</template>
